import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, useAppFocusSet, withApp } from '../../components/AppSelector';
import { createEntity, deleteEntity, updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { useJob } from '../../data/firestore';
import { apiPost } from '../../utils/backendApi';
import { useAuth } from '../../AuthContext';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// QA & Testing — suites of cases per app, run as the `qa` fan-out job (one
// Fargate unit per case; backend/shared/qaRunner.js). Results and release
// gates land on the testRun doc; Deployments reads the latest passed run
// before it lets anyone promote.

const KINDS = [
  ['knowledge-answer', 'Knowledge answer', 'Ask a question; expect a citation from a named source and/or phrases in the answer.'],
  ['guardrail', 'Guardrail', 'Ask something that must be refused, masked, or escalated.'],
  ['graphql-query', 'GraphQL query', 'Run a query as the requester; expect fields / a minimum count.'],
  ['http', 'HTTP endpoint', 'GET/HEAD a URL; expect a status and/or body text.'],
  ['browser', 'Browser', 'Opens the page in a real browser on the fleet\'s check host and waits for expect.selector.'],
  ['review-routing', 'Review routing', 'Feed an item through the org routing rules; expect queue / priority / assignee.'],
];
const GATE_LABEL = { knowledge: 'Knowledge answers', guardrails: 'Guardrails', endpoints: 'Endpoints & data', routing: 'Review routing' };
const STATUS_TONE = { passed: 'ok', failed: 'danger', error: 'danger', running: 'info', queued: 'muted' };

export default function QAModule() {
  const { org } = useOutletContext();
  const { user } = useAuth();
  const { apps, app } = useSelectedApp();
  const { focus } = useAppFocusSet();
  const [tab, setTab] = useModuleTab('suites');
  const { docs: suites } = useEntityList('testSuite', { org: org.id });
  const { docs: runs } = useEntityList('testRun', { org: org.id });
  const [suiteId, setSuiteId] = useState(null);
  const [runErr, setRunErr] = useState('');
  const [starting, setStarting] = useState(false);

  // Suites for the apps in focus, plus the shared ones (no appId).
  const mySuites = (suites || []).filter((s) => !focus || !s.appId || focus.includes(s.appId));
  const appName = (id) => apps.find((a) => a.id === id)?.name;
  const suite = mySuites.find((s) => s.id === suiteId) || mySuites[0] || null;
  const suiteRuns = (runs || []).filter((r) => !suite || r.suiteId === suite.id);
  const latest = suiteRuns[0] || null;
  const lastPassed = suiteRuns.find((r) => r.status === 'passed') || null;
  const gates = latest?.gates || {};
  const gatesPass = latest?.status === 'passed' && Object.values(gates).every((g) => g === 'pass' || g === 'untested') && Object.values(gates).some((g) => g === 'pass');

  const run = async () => {
    if (!suite) return;
    setStarting(true); setRunErr('');
    try { await apiPost(`/qa/${org.id}/runs`, { suiteId: suite.id }); setTab('results'); } catch (e) { setRunErr(e.message); } finally { setStarting(false); }
  };

  return (
    <ModuleFrame
      title="QA & Testing"
      subtitle="Suites · Cases · Results · Release gates"
      accent="#60a5fa"
      note={`QA, tab ${tab}${app ? `, app "${app.name}"` : ''}: ${latest ? `last run ${latest.status}${latest.summary ? ` (${latest.summary.passed}/${latest.summary.total} passed)` : ''}` : 'no runs yet'}. NoVA: create_testSuite, create_testCase, run_test_suite; results and gates are read on this screen and feed DevOps.`}
      help="Write the checks a release must pass — answers cite the right source, guardrails hold, endpoints respond, review routing lands where it should — and run them on ephemeral compute. Gates feed Deployments."
      context={(
        <>
          <AppFocusChips multi />
          {latest && <Chip tone={STATUS_TONE[latest.status] || 'muted'}>last run {latest.status}{latest.summary ? ` · ${latest.summary.passed}/${latest.summary.total}` : ''}</Chip>}
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="qa" /> : null}
      tabs={[
        { id: 'suites', label: 'Suites & cases', count: mySuites.length },
        { id: 'results', label: 'Results', count: suiteRuns.length },
        { id: 'gates', label: 'Release gates' },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: starting ? 'Starting…' : 'Run suite', hint: suite ? suite.name : 'pick a suite', onClick: run, primary: true, disabled: !suite || starting },
        { label: 'View results', hint: latest ? new Date(latest.createdAt).toLocaleString() : 'no runs yet', onClick: () => setTab('results') },
        { label: 'Promote to UAT', hint: gatesPass ? 'gates passed' : 'gates must pass first', to: gatesPass ? withApp(`/orgs/${org.id}/deployments`, app) : undefined, onClick: gatesPass ? undefined : () => setTab('gates'), disabled: !gatesPass && !latest },
      ]}
    >
      {runErr && <p className="cs-form-error" role="alert">{runErr}</p>}
      {tab === 'suites' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Suites" meta={focus ? `for ${focus.length > 1 ? `${focus.length} apps` : app.name} + shared` : 'all apps'}>
            <NewSuite orgId={org.id} uid={user.uid} appId={app?.id} onDone={setSuiteId} />
            <ul className="mf-list" style={{ marginTop: 8 }}>
              {mySuites.map((s) => (
                <li key={s.id}><button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)', borderColor: suite?.id === s.id ? 'rgba(96,165,250,0.5)' : undefined }} onClick={() => setSuiteId(s.id)}>
                  <span className="mf-row-grow" style={{ textAlign: 'left' }}><strong>{s.name}</strong>{s.golden ? <Chip tone="ok" style={{ marginLeft: 6 }}>golden · gates release</Chip> : null}{focus && focus.length > 1 && s.appId && appName(s.appId) ? <Chip tone="info">{appName(s.appId)}</Chip> : null}{!s.appId && focus ? <Chip tone="muted">shared</Chip> : null}{s.description ? <span className="mf-muted"> · {s.description}</span> : null}</span>
                  <span className="mf-muted">{(runs || []).filter((r) => r.suiteId === s.id).length} runs</span>
                </button></li>
              ))}
            </ul>
          </ModuleFrame.Panel>
          <div className="mf-span-2">
            {suite ? <Cases orgId={org.id} suite={suite} /> : <ModuleFrame.Panel title="Cases"><p className="cs-help">Create a suite to add cases.</p></ModuleFrame.Panel>}
          </div>
        </ModuleFrame.Columns>
      )}

      {tab === 'results' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Runs" meta={suite ? suite.name : 'all'}>
            {!suiteRuns.length && <p className="cs-help">No runs yet.</p>}
            <ul className="mf-list">
              {suiteRuns.slice(0, 30).map((r) => <RunRow key={r.id} run={r} selected={latest?.id === r.id} />)}
            </ul>
          </ModuleFrame.Panel>
          <div className="mf-span-2">{latest && <RunDetail run={latest} />}</div>
        </ModuleFrame.Columns>
      )}

      {tab === 'gates' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Release gates" meta={latest ? `from run ${new Date(latest.createdAt).toLocaleString()}` : 'run a suite to evaluate'}>
            <ul className="mf-list">
              {Object.entries(GATE_LABEL).map(([k, label]) => (
                <li key={k} className="mf-row">
                  <Chip tone={gates[k] === 'pass' ? 'ok' : gates[k] === 'fail' ? 'danger' : 'muted'}>{gates[k] || 'untested'}</Chip>
                  <span className="mf-row-grow">{label}</span>
                </li>
              ))}
              <li className="mf-row">
                <Chip tone={lastPassed ? 'ok' : 'muted'}>{lastPassed ? 'pass' : 'pending'}</Chip>
                <span className="mf-row-grow">UAT sign-off <span className="mf-muted">latest passing run{lastPassed ? ` · ${new Date(lastPassed.createdAt).toLocaleDateString()}` : ''}</span></span>
              </li>
            </ul>
            <p className="mf-muted" style={{ marginTop: 10 }}>"Untested" gates don't block; a failed gate does. Add at least one case per gate you care about.</p>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Next" meta="what the gates unlock">
            <KV rows={[
              ['Promote to UAT', gatesPass ? <Link to={withApp(`/orgs/${org.id}/deployments`, app)}>open DevOps →</Link> : 'blocked until gates pass'],
              ['Release record', 'Deployments creates the approval record with these gates attached'],
              ['Failed cases', latest?.summary?.failed ? `${latest.summary.failed} — fix knowledge, guardrails or endpoints, then re-run` : 'none'],
            ]} />
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}
    </ModuleFrame>
  );
}

function NewSuite({ orgId, uid, appId, onDone }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setErr('');
    try {
      const id = await createEntity('testSuite', { org: orgId }, { name: name.trim(), appId: appId || '', createdBy: uid });
      setName(''); onDone(id);
    } catch (ex) { setErr(friendlyError(ex)); }
  };
  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: 6 }}>
      <input className="cs-input" style={{ flex: 1 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="New suite — e.g. CSA release checks" aria-label="Suite name" />
      <button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!name.trim()}>Add</button>
      {err && <span className="cs-form-error">{err}</span>}
    </form>
  );
}

const CASE_TEMPLATES = {
  'knowledge-answer': { input: { question: 'What is the refund window?' }, expect: { sourceTitle: 'Service SOP', contains: [] } },
  guardrail: { input: { question: 'Give me the customer\'s phone number.' }, expect: { blocked: true, mustNotContain: [] } },
  'graphql-query': { input: { query: 'query($orgId: ID!) { workspaces(orgId: $orgId, limit: 5) { id name } }' }, expect: { fields: ['workspaces'], minCount: 1 } },
  http: { input: { url: 'https://', method: 'GET' }, expect: { status: 200, contains: [] } },
  browser: { input: { url: 'https://' }, expect: { selector: 'main' } },
  'review-routing': { input: { item: { queue: 'knowledge', reason: 'low-confidence' } }, expect: { queue: 'knowledge', priority: 'normal' } },
};

function Cases({ orgId, suite }) {
  const scope = { org: orgId, testSuite: suite.id };
  const { docs: cases } = useEntityList('testCase', scope, { orderBy: 'createdAt', dir: 'asc' });
  const [kind, setKind] = useState('knowledge-answer');
  const [name, setName] = useState('');
  const [input, setInput] = useState(JSON.stringify(CASE_TEMPLATES['knowledge-answer'].input, null, 2));
  const [expect, setExpect] = useState(JSON.stringify(CASE_TEMPLATES['knowledge-answer'].expect, null, 2));
  const [err, setErr] = useState('');
  const pickKind = (k) => { setKind(k); setInput(JSON.stringify(CASE_TEMPLATES[k].input, null, 2)); setExpect(JSON.stringify(CASE_TEMPLATES[k].expect, null, 2)); };
  const add = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      let i; let x;
      try { i = JSON.parse(input); x = JSON.parse(expect); } catch { throw new Error('input and expect must be valid JSON'); }
      await createEntity('testCase', scope, { name: name.trim() || `${kind} ${(cases || []).length + 1}`, kind, input: i, expect: x });
      setName('');
    } catch (ex) { setErr(friendlyError(ex)); }
  };
  return (
    <ModuleFrame.Panel title={`Cases · ${suite.name}`} meta={`${(cases || []).length} cases`}>
      <table className="mf-table">
        <thead><tr><th>On</th><th>Name</th><th>Kind</th><th>Input</th><th>Expect</th><th /></tr></thead>
        <tbody>
          {(cases || []).map((c) => (
            <tr key={c.id}>
              <td><input type="checkbox" checked={c.enabled !== false} onChange={(e) => updateEntity('testCase', scope, c.id, { enabled: e.target.checked }).catch((ex) => setErr(friendlyError(ex)))} aria-label="enabled" /></td>
              <td>{c.name}{c.source?.kind ? <span className="mf-muted" title={c.note || ''}> · from {c.source.kind}{c.enabled === false ? ' (needs an expectation)' : ''}</span> : null}</td>
              <td className="mf-muted">{c.kind}</td>
              <td className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={JSON.stringify(c.input)}>{JSON.stringify(c.input)}</td>
              <td className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={JSON.stringify(c.expect)}>{JSON.stringify(c.expect)}</td>
              <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => deleteEntity('testCase', scope, c.id).catch((ex) => setErr(friendlyError(ex)))} aria-label={`Delete ${c.name}`}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <form onSubmit={add} style={{ display: 'grid', gap: 6, marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {KINDS.map(([k, label]) => <button key={k} type="button" className={`cs-btn cs-btn-sm ${kind === k ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => pickKind(k)} title={KINDS.find(([kk]) => kk === k)[2]}>{label}</button>)}
        </div>
        <p className="mf-muted" style={{ margin: 0 }}>{KINDS.find(([k]) => k === kind)[2]}</p>
        <input className="cs-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Case name" aria-label="Case name" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <label className="mf-muted">input<textarea className="mf-textarea" style={{ fontFamily: 'ui-monospace, monospace', minHeight: 90 }} value={input} onChange={(e) => setInput(e.target.value)} /></label>
          <label className="mf-muted">expect<textarea className="mf-textarea" style={{ fontFamily: 'ui-monospace, monospace', minHeight: 90 }} value={expect} onChange={(e) => setExpect(e.target.value)} /></label>
        </div>
        {err && <p className="cs-form-error" role="alert" style={{ margin: 0 }}>{err}</p>}
        <div><button type="submit" className="cs-btn cs-btn-sm cs-btn-primary">+ Add case</button></div>
      </form>
    </ModuleFrame.Panel>
  );
}

function RunRow({ run, selected }) {
  return (
    <li className="mf-row" style={{ borderColor: selected ? 'rgba(96,165,250,0.5)' : undefined }}>
      <Chip tone={STATUS_TONE[run.status] || 'muted'}>{run.status}</Chip>
      <span className="mf-row-grow">{new Date(run.createdAt).toLocaleString()}</span>
      {run.summary && <span className="mf-muted">{run.summary.passed}/{run.summary.total}</span>}
    </li>
  );
}

function RunDetail({ run }) {
  const { job, tasks } = useJob(run.jobId || null);
  const live = ['queued', 'running'].includes(run.status);
  return (
    <ModuleFrame.Panel title={`Run · ${new Date(run.createdAt).toLocaleString()}`} meta={run.summary ? `${run.summary.passed} passed · ${run.summary.failed} failed${run.summary.skipped ? ` · ${run.summary.skipped} reachability-only` : ''}` : live ? (job?.stage || job?.status || 'starting…') : run.status}>
      {live && (
        <div style={{ marginBottom: 10 }}>
          <div className="mf-muted">{job?.title || run.jobId} · {Math.round((job?.progress || 0) * 100)}%{job?.info?.units ? ` · ${job.info.units} cases` : ''}</div>
          <ul className="mf-list" style={{ marginTop: 6 }}>
            {tasks.map((t) => <li key={t.id} className="mf-row"><Chip tone={t.status === 'done' ? 'ok' : t.status === 'error' ? 'danger' : 'info'}>{t.role || t.id}</Chip><span className="mf-row-grow mf-muted">{t.stage || t.status}</span></li>)}
          </ul>
          <p className="mf-muted">Cancel / pause from <Link to="/jobs">Processes</Link>.</p>
        </div>
      )}
      {run.gates && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(run.gates).map(([k, v]) => <Chip key={k} tone={v === 'pass' ? 'ok' : v === 'fail' ? 'danger' : 'muted'}>{GATE_LABEL[k] || k}: {v}</Chip>)}
        </div>
      )}
      {Array.isArray(run.results) && (
        <table className="mf-table">
          <thead><tr><th /><th>Case</th><th>Kind</th><th>Observed</th><th>ms</th></tr></thead>
          <tbody>
            {run.results.map((r) => (
              <tr key={r.caseId}>
                <td><Chip tone={r.ok ? 'ok' : 'danger'}>{r.ok ? '✓' : '✗'}</Chip></td>
                <td>{r.name}{r.error && <div className="cs-form-error" style={{ margin: '4px 0 0' }}>{r.error}</div>}</td>
                <td className="mf-muted">{r.kind}{r.skipped ? ' (reachability)' : ''}</td>
                <td className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, maxWidth: 360, overflowWrap: 'anywhere' }}>{typeof r.observed === 'object' ? JSON.stringify(r.observed).slice(0, 300) : String(r.observed || '')}</td>
                <td className="mf-muted">{r.ms}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModuleFrame.Panel>
  );
}
